import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Searchbox from 'infrastructure/js/components/controls/Searchbox/searchbox';
import Pagination from 'infrastructure/js/components/controls/Pagination/pagination';
import { api as aiAssistantApi } from '../../../actions/AiAssistant/aiAssistantActions';
import AiAssistantHistoryRow from './aiAssistantHistoryRow';
import RobotIcon from '../../../../assets/svg/ai-agent-robot.svg';
import './aiAssistantHistoryList.scss';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export default function AiAssistantHistoryList({ labels, onOpenConversation, onStartNewChat }) {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [queryResultInfo, setQueryResultInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const searchTimeoutRef = useRef(null);

  const load = useCallback((nextQuery, nextPageNumber) => {
    setLoading(true);
    setError(false);
    aiAssistantApi.fetchConversations({ query: nextQuery, pageNumber: nextPageNumber, pageSize: PAGE_SIZE }).then((result) => {
      setLoading(false);
      if (!result) {
        setError(true);
        return;
      }
      setConversations(result.dataList);
      setQueryResultInfo(result.queryResultInfo);
    });
  }, []);

  useEffect(() => {
    load(query, pageNumber);
  }, [load, query, pageNumber]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPageNumber(0);
      setQuery(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleDelete = (sessionId) => {
    aiAssistantApi.deleteConversation(sessionId, () => load(query, pageNumber));
  };

  const handlePreviousPage = () => setPageNumber((n) => Math.max(0, n - 1));
  const handleNextPage = () => setPageNumber((n) => n + 1);

  const paginationData = queryResultInfo
    ? { ...queryResultInfo, numberOfElements: conversations.length }
    : null;
  const showPagination = !loading && !error && paginationData && paginationData.totalElements > 0;
  const showEmptyState = !loading && !error && conversations.length === 0;
  const showRows = !loading && !error && conversations.length > 0;

  return (
    <div className="ais-history-list">
      <div className="ais-history-list-toolbar">
        <Searchbox
          value={searchInput}
          onChange={handleSearchChange}
          placeholder={labels.get('history.search.placeholder', 'Search conversations')}
        />
        <button className="ais-history-new-chat-btn" onClick={onStartNewChat}>
          <i className="fa fa-plus" />
          {labels.get('history.button.newChat', 'New chat')}
        </button>
      </div>

      <div className="ais-history-list-body">
        {loading && <div className="ais-history-state">{labels.get('history.loading', 'Loading...')}</div>}
        {!loading && error && (
          <div className="ais-history-state">{labels.get('history.error.loadFailed', 'Failed to load conversation history.')}</div>
        )}
        {showEmptyState && (
          <div className="ais-history-state ais-history-empty">
            <RobotIcon />
            <p>{labels.get('history.empty', 'No past conversations yet.')}</p>
          </div>
        )}
        {showRows && conversations.map((conversation) => (
          <AiAssistantHistoryRow
            key={conversation.sessionId}
            labels={labels}
            conversation={conversation}
            onOpen={() => onOpenConversation(conversation.sessionId)}
            onDelete={() => handleDelete(conversation.sessionId)}
          />
        ))}
      </div>

      {showPagination && (
        <Pagination paginationData={paginationData} onPreviousPageClick={handlePreviousPage} onNextPageClick={handleNextPage} />
      )}
    </div>
  );
}

AiAssistantHistoryList.propTypes = {
  labels: PropTypes.object.isRequired,
  onOpenConversation: PropTypes.func.isRequired,
  onStartNewChat: PropTypes.func.isRequired,
};
